import { MedRegBewilligungDto, MedRegWbtDto, MedRegWholePersonDetailedDto, PersonLicenceType, PsyRegAuthorisationDto, PsyRegPersonDetailedDto } from '@shared/service-proxies/service-proxies';
import { IHealthRegisterFullPersonInfo } from '../health-register.service';
import { AppConsts } from '@shared/AppConsts';
import { DateTime } from '@node_modules/@types/luxon';
import { Observable } from '@node_modules/rxjs/dist/types';
import { HealthRegisterDiploma } from './health-register-diploma';
import { HealthRegisterPostgraduateTitle } from './health-register-postgraduate-title';
import { HealthRegisterPostgraduateTitlePrivateLaw } from './health-register-postgraduate-title-private-law';
import { HealthRegisterBaseService } from '../health-register-base.service';
import { DatePipe } from '@node_modules/@angular/common';
import { Injector } from '@node_modules/@angular/core';

export class PsyRegFullPersonInfo extends HealthRegisterBaseService implements IHealthRegisterFullPersonInfo  {

    cantonId: string;

    constructor(
        private latestPsyRegPerson: PsyRegPersonDetailedDto,
        protected datePipe: DatePipe,
        injector: Injector,
    ) {
        super(datePipe, injector);
    }

    get healthRegisterLabel(): string {
        return 'PsyReg';
    }

    get SearchSuccessfulMessage(): string {
        return this.l('SearchSuccessfulPsyRegMessage');
    }

    get SearchFailedMessageKey(): string {
        return 'SearchFailedPsyRegMessage';
    }

    personWasFound(): boolean {
        return !!this.latestPsyRegPerson;
    }

    get fullName(): string {
        let person = this.latestPsyRegPerson;
        let name = person?.firstName;

        if (person?.name) {
            if (name) {
                name = name + ' ' + person.name;
            } else {
                name = person.name;
            }
        }

        return name;
    }

    get birthYear(): string {
        return this.latestPsyRegPerson?.birthYear;
    }

    get nationality1(): string {
        return this.latestPsyRegPerson?.nationality1.sironaId;
    }

    get nationality2(): string {
        return this.latestPsyRegPerson?.nationality2?.sironaId;
    }

    get queryTime(): DateTime {
        return this.latestPsyRegPerson?.queryTime;
    }

    get ahv(): string {
        return null;
    }

    get gender(): string {
        return this.latestPsyRegPerson?.gender.sironaId;
    }

    get gln(): string {
        return this.latestPsyRegPerson?.gln;
    }

    get firstName(): string {
        return this.latestPsyRegPerson?.firstName;
    }

    get lastName(): string {
        return this.latestPsyRegPerson?.name;
    }

    get birthName(): string {
        return null;
    }

    get placeOfOrigin(): string {
        return null;
    }

    get languageSkills(): string[] {
        if (this.latestPsyRegPerson == null) {
            return [];
        }

        return this.latestPsyRegPerson.languageSkills.filter(x => x.sironaId !== null).map(x => x.sironaId);
    }

    get languageSkillsAsCommaSeparatedList(): string {
        let skills = this.languageSkills;
        return skills?.length > 0 ? skills.map(s => this.localization.localize(s, this.lSource)).join(', ') : null;
    }

    get diplomas(): string[] {
        let list: string[] = [];
        let diploma = this.latestPsyRegPerson?.psyRegEducations?.filter(x => x.isPostgraduateTitle === false);
        if (diploma) {
            for (let i = 0; i < diploma.length; i++) {
                list.push(
                    `${this.localization.localize(diploma[i].profession.sironaId, this.lSource)}, ${this.resolveDate(diploma[i].issuingDate)}`
                );
            }
        }
        return list;
    }

    //private displayCorrectDiplomaDate(diploma: PsyRegEducationDto): string {
    //    if (diploma.typeId == AppConsts.codes.diploma.psyreg.type.federalPsyReg) {
    //        return this.resolveDate(diploma.issuingDate);
    //    }

    //    return this.resolveDate(diploma.recognitionDate);
    //}

    getProfessionDiplomaForComparison(professionId: string): HealthRegisterDiploma {

        let diplomas = this.latestPsyRegPerson?.psyRegEducations?.filter(x => x.isPostgraduateTitle === false);
        if (diplomas) {
            const diploma = diplomas.find(x => x.profession.sironaId === professionId) ?? diplomas.find(x => x.profession.sironaId === this.codes.profession.psychologist);
            if (diploma) {
                const healthRegisterDiploma = new HealthRegisterDiploma();
                healthRegisterDiploma.professionId = diploma.profession.sironaId;
                healthRegisterDiploma.typeId = diploma.typeId;
                healthRegisterDiploma.countryId = diploma.issuingCountry.sironaId;
                healthRegisterDiploma.dateOfIssue = this.resolveDate(diploma.issuingDate);
                healthRegisterDiploma.dateOfRecognition = this.resolveDate(diploma.recognitionDate);
                healthRegisterDiploma.placeOfIssue = diploma.issuingPlace;
                return healthRegisterDiploma;
            }

            return null;
        }
    }

    getLicences(): PsyRegAuthorisationDto[] {
        return this.latestPsyRegPerson?.psyRegAuthorisations;
    }

    get normalLicences(): string[] {
        const list = [];
        let licences = this.getLicences()?.filter((l) => l.licenceType !== PersonLicenceType.ProfessionalNinetyDays && l.licenceType !== PersonLicenceType.ExpenceHealthCareNinetyDays);
        if (licences) {
            for (let i = 0; i < licences.length; i++) {
                list.push(
                    `${this.localization.localize(licences[i].profession, this.lSource)},
                    ${this.localization.localize(licences[i].canton.sironaId, this.lSource)},
                    ${this.resolveDate(licences[i].decisionDate)},
                    ${this.localization.localize(licences[i].licenceStatus?.healthRegisterDescription, this.lSource)}`
                );
            }
        }

        return list;
    }

    hasLicenceInThisCanton(): boolean {
        let licences = this.getLicences();
        let hasLicenceInThisCanton = licences.some(x => x.canton.sironaId === abp.canton.code);
        return hasLicenceInThisCanton;
    }

    get dl90Licences(): string[] {
        const list = [];
        let licences = this.getLicences()?.filter((l) => l.licenceType === PersonLicenceType.ProfessionalNinetyDays || l.licenceType === PersonLicenceType.ExpenceHealthCareNinetyDays);
        if (licences) {
            for (let i = 0; i < licences.length; i++) {
                list.push(
                    `${this.localization.localize(licences[i].profession, this.lSource)},
                    ${this.localization.localize(licences[i].canton.sironaId, this.lSource)},
                    ${this.resolveDate(licences[i].decisionDate)},
                    ${this.localization.localize(licences[i].licenceStatus?.healthRegisterDescription, this.lSource)}`
                );
            }
        }

        return list;
    }

    get hasSensitiveData(): boolean {
        const dataDeservingProtection = this.latestPsyRegPerson.dataDeservingProtection;
        return dataDeservingProtection === true;
    }

    get hasSensitiveDataCantonalLaw(): boolean {
        return false;
    }

    get cantonalLicenceRestrictions(): string[] {
        return this.latestPsyRegPerson?.cantonalLicenceRestrictions;
    }

    // getLanguage(psyRegId: number): Promise<string> {
    //     return new Promise((resolve) => {
    //         this.getPerson(psyRegId).subscribe((output: PsyRegPersonDetailedDto) => {
    //             let language;
    //             const languageOfCorrespondence = output.languageOfCorrespondence.sironaId;
    //             if (
    //                 languageOfCorrespondence === AppConsts.codes.language.german ||
    //                 languageOfCorrespondence === AppConsts.codes.language.french
    //             ) {
    //                 language = languageOfCorrespondence;
    //             }
    //             resolve(language);
    //         });
    //     });
    // }

    get birthDate(): string {
        return this.resolveDate(this.latestPsyRegPerson?.birthdate);
    }

    get postgraduateTitles(): string[] {
        let list: string[] = [];
        let titles = this.latestPsyRegPerson?.psyRegEducations.filter(x => x.isPostgraduateTitle === true);
        if (titles) {
            for (let i = 0; i < titles.length; i++) {
                list.push(
                    this.localization.localize(titles[i].typeId, this.lSource) +
                    ' ' +
                    this.resolveLanguage(titles[i].postgraduateKindLabel) +
                    ', ' +
                    this.resolveDate(titles[i].issuingDate)
                );
            }
        }
        return list;
    }

    // getPostgraduateTitlesForPerson(registerPersonId: number): Observable<string[] | string> {
    //     return this.getPerson(registerPersonId).pipe(map((output: PsyRegPersonDetailedDto) => output.psyRegEducations.filter(x => x.isPostgraduateTitle === true).map((wbt: PsyRegEducationDto) => wbt.typeId)));
    // }

    getPostgraduateTitlesForComparison(): HealthRegisterPostgraduateTitle[] {
        let titles = this.latestPsyRegPerson?.psyRegEducations.filter(x => x.isPostgraduateTitle == true);
        if (titles) {
            const healthRegisterPostgraduateTitles: HealthRegisterPostgraduateTitle[] = [];
            for (let i = 0; i < titles.length; i++) {
                const healthRegisterPostgraduateTitle = new HealthRegisterPostgraduateTitle();
                healthRegisterPostgraduateTitle.titleId = titles[i].kind.sironaId;
                healthRegisterPostgraduateTitle.typeId = titles[i].typeId;
                healthRegisterPostgraduateTitle.dateOfIssue = this.resolveDate(titles[i].issuingDate);
                healthRegisterPostgraduateTitle.dateOfRecognition = this.resolveDate(titles[i].recognitionDate);
                healthRegisterPostgraduateTitle.placeOfIssue = titles[i].issuingPlace;
                healthRegisterPostgraduateTitle.countryId = titles[i].issuingCountry.sironaId;
                healthRegisterPostgraduateTitles.push(healthRegisterPostgraduateTitle);
            }
            return healthRegisterPostgraduateTitles;
        }
    }

    getPostgraduateTitlesPrivateLawForComparison(): HealthRegisterPostgraduateTitlePrivateLaw[] {
        return [];
    }

    get hasInactiveLicences(): boolean {
        const licences = this.getLicences();
        return licences?.some((l) => l.licenceStatus.sironaId !== AppConsts.codes.professionalLicenceStatus.granted);
    }

    get hasRestrictedLicences(): boolean {
        const licences = this.getLicences();
        return licences?.some((l) => l.licenceStatus.sironaId === AppConsts.codes.professionalLicenceStatus.restricted);
    }

    get hasInactiveLicencesInOtherCantons(): boolean {
        const licences = this.getLicences();
        return licences?.some((l) => l.licenceStatus.sironaId !== AppConsts.codes.professionalLicenceStatus.granted && l.canton.sironaId !== this.cantonId);
    }

    get hasInactiveLicencesInCanton(): boolean {
        const licences = this.getLicences();
        return licences?.some((l) => l.licenceStatus.sironaId !== AppConsts.codes.professionalLicenceStatus.granted && l.canton.sironaId === this.cantonId);
    }
}

